<template>
  <div class="supplier-public-profile">
    <LoadingBar v-if="loading" message="Loading Profile..." />
    <div v-else-if="loadError" class="profile-status profile-status--error">{{ loadError }}</div>

    <template v-else-if="profile">
      <header class="profile-banner">
        <div class="profile-banner__inner">
          <div class="profile-banner__identity">
            <div class="profile-banner__avatar">
              <img v-if="profile.photo" :src="getFileUrl(profile.photo)" :alt="profile.company_name"
                class="profile-banner__avatar-img " style="height:100px; width:100px" />
              <span v-else>
                {{ initials }}
              </span>
            </div>

            <div class="profile-banner__text">
              <h1 class="profile-banner__name">{{ profile.company_name }}</h1>

              <p class="profile-banner__meta">
                <span v-if="location">{{ location }}</span>
                <span v-if="location && memberSince" class="profile-banner__dot">·</span>
                <span v-if="memberSince">Member since {{ memberSince }}</span>
              </p>

              <p v-if="contactName" class="profile-banner__contact">
                Contact: {{ contactName }}
              </p>
            </div>
          </div>
          <div class="profile-banner__actions">
            <router-link :to="{ name: 'SearchNetwork' }" class="app-header__support">
              <i class="fas fa-arrow-left me-2"></i>
              Back to Search
            </router-link>
            <button v-if="isLoggedIn && profile.id !== mySupplierId" type="button" class="my-supplier-btn"
              :class="`my-supplier-btn--${mySupplierStatus || 'none'}`" :disabled="mySupplierButtonDisabled"
              @click="addToMySuppliers">
              <i class="fas" :class="mySupplierButtonIcon"></i>
              {{ mySupplierButtonLabel }}
            </button>


          </div>
        </div>
        
      </header>
      <div class="profile-container mt-3">
     
        <!-- ============ Services ============ -->
        <section class="profile-section mt-3">
          <div class="section-head">
            <span class="section-head__icon">🤝</span>
            <div>
              <h4 class="section-head__title">Services</h4>
              <p class="section-head__desc">Services offered.</p>
            </div>
          </div>

          <div v-if="services.length" class="chip-row">
            <span v-for="service in services" :key="service.id" class="chip">{{ service.name }}</span>
          </div>
          <p v-else class="profile-empty">No services listed yet.</p>
        </section>
        <div class="section-head"> <span class="section-head__icon">📦</span>
          <div>
            <h4 class="section-head__title">Materials</h4>
            <p class="section-head__desc">Materials listed.</p>
          </div>
        </div>
      <!-- ============ Materials ============ -->
      <table v-if="materials.length" class="data-table">
        <thead>
          <tr>
            <th>Sr.</th>
            <th>Name</th>
            <th>Type</th>
            <th>Pack Size</th>
            <th>Lead Time</th>
            <th>Sub Ingredients</th>

            <th v-if="this.mySupplierStatus === 'approved'">Documents</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(material, index) in materials" :key="material.id">
            <td class="data-table__strong">{{ index + 1 }}</td>
            <td class="data-table__strong">{{ material.name }}</td>
            <td class="data-table__muted">{{ formatType(material.type) }}</td>
            <td class="data-table__muted">{{ material.pack_size || '—' }}</td>
            <td class="data-table__muted">{{ material.lead_time || '—' }}</td>
            <td class="data-table__muted">{{ material.sub_ingredients || '—' }}</td>
            <td class="asp-td-documents" v-if="this.mySupplierStatus === 'approved'">
              <div v-if="material.documents && material.documents.length" class="document-list">
                <a v-for="(doc, idx) in material.documents" 
                  :key="idx" 
                  :href="getFileUrl(doc.path)" 
                  target="_blank" 
                  class="document-link"
                  :title="doc.org_name || doc.name">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                  {{ doc.org_name || doc.name || 'Document' }}
                </a>
              </div>
              <span v-else class="asp-td-muted">—</span>
            </td>
          </tr>
        </tbody>
      </table>
        <!-- ============ Products ============ -->
        <section class="profile-section mt-3">
          <div class="section-head">
            <span class="section-head__icon">🏷️</span>
            <div>
              <h4 class="section-head__title">Products</h4>
              <p class="section-head__desc">Products listed.</p>
            </div>
          </div>

          <table v-if="products.length" class="data-table">
            <thead>
              <tr>
                <th>Sr.</th>
                <th>Name</th>
                <th>Production Type</th>
                <th>Unit</th>
                <th>SKU</th>
                <th>Application</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(product, index) in products" :key="product.id">
                <td class="data-table__strong">{{ index + 1 }}</td>
                <td class="data-table__strong">{{ product.name }}</td>
                <td class="data-table__muted">
                  {{ formatProductionType(product.production_type) }}
                </td>
                <td class="data-table__muted">{{ product.unit || '—' }}</td>
                <td class="data-table__muted">{{ product.sku || '—' }}</td>
                <td class="data-table__muted">
                  {{ formatType(product.application) }}
                </td>
              </tr>
            </tbody>
          </table>

          <p v-else class="profile-empty">
            No products listed yet.
          </p>
        </section>
   <SupplierReviews
          v-if="profile && !loading && isLoggedIn"
          :supplier-id="profile.id"
          :supplier-name="profile.company_name"
        />
      </div>

      <button v-if="isLoggedIn" type="button" class="chat-fab" :class="{ 'is-open': chatOpen }"
        :aria-label="chatOpen ? 'Close chat' : 'Connect Now'" @click="toggleChat">
        <svg v-if="!chatOpen" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
          stroke-linecap="round" stroke-linejoin="round">
          <path
            d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5 8.4 8.4 0 0 1-3.9-.94L3 20l1.06-3.9A8.4 8.4 0 0 1 3 11.5 8.5 8.5 0 0 1 20.5 8" />
        </svg>
        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"
          stroke-linejoin="round">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
        <span class="chat-fab__label">Connect Now</span>
      </button>

      <!-- ============ Chat panel ============ -->
      <teleport to="body">
        <transition name="chat-pop">
          <aside v-if="chatOpen" class="chat-panel">
            <header class="chat-panel__header">
              <div class="chat-panel__identity">
                <div class="chat-panel__avatar">{{ initials }}</div>
                <div>
                  <strong class="chat-panel__name">{{ profile.company_name }}</strong>
                  <span class="chat-panel__status">
                    {{ starting ? 'Starting conversation…' : conversationId ? 'Online' : 'Unable to connect' }}
                  </span>
                </div>
              </div>
              <button type="button" class="chat-panel__close" aria-label="Close" @click="chatOpen = false">✕</button>
            </header>

            <div ref="messageList" class="chat-panel__messages">
              <p v-if="loadingHistory" class="chat-panel__empty">Loading conversation…</p>
              <p v-else-if="starting" class="chat-panel__empty">Connecting you with {{ profile.company_name }}…</p>
              <p v-else-if="chatError" class="chat-panel__empty chat-panel__empty--error">
                {{ chatError }}
                <button type="button" class="chat-panel__retry" @click="startConversation">Try again</button>
              </p>
              <p v-else-if="!messages.length" class="chat-panel__empty">
                Start the conversation with {{ profile.company_name }}.
              </p>

              <div v-for="(msg, i) in messages" :key="i" class="chat-msg" :class="{ 'chat-msg--mine': msg.mine }">
                <p v-if="msg.text" class="chat-msg__text">{{ msg.text }}</p>
                <div v-if="msg.attachment" class="chat-msg__attachment">

                  <img v-if="msg.attachment.type?.startsWith('image/')" :src="msg.attachment.url"
                    class="chat-msg__image" />

                  <a v-else :href="msg.attachment.url" target="_blank" class="chat-msg__file">
                    📎 {{ msg.attachment.name }}
                  </a>

                </div>
                <span class="chat-msg__time">{{ msg.time }}</span>
              </div>
            </div>

            <div class="chat-panel__composer">
              <div v-if="pendingFiles.length" class="chat-panel__attachment-preview">
                <div class="attachment-card">

                  <img v-if="pendingFiles[0].type.startsWith('image/')" :src="URL.createObjectURL(pendingFiles[0])"
                    class="attachment-preview-image" />

                  <div v-else class="attachment-preview-file">
                    <i class="fas fa-file-alt"></i>
                  </div>

                  <div class="attachment-info">
                    <div class="attachment-name">
                      {{ pendingFiles[0].name }}
                    </div>

                    <div class="attachment-size">
                      {{ (pendingFiles[0].size / 1024).toFixed(1) }} KB
                    </div>
                  </div>

                  <button type="button" class="attachment-remove" @click="removeFile(0)">
                    ✕
                  </button>

                </div>
              </div>
              <div class="chat-panel__input-row">
                <button type="button" class="chat-panel__attach" aria-label="Attach file"
                  @click="$refs.fileInput.click()">📎</button>
                <input ref="fileInput" type="file" class="chat-panel__file-input" @change="onFilesSelected" />
                <textarea v-model="draftMessage" class="chat-panel__textarea" rows="2" placeholder="Write a message..."
                  :disabled="!conversationId || sending" @keydown.enter.exact.prevent="sendMessage"></textarea>
                <button type="button" class="chat-panel__send" :disabled="!canSend" @click="sendMessage">
                  {{ sending ? '…' : 'Send' }}
                </button>
              </div>
            </div>
          </aside>
        </transition>
      </teleport>
    </template>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import { resolveFileUrl as resolveAssetFileUrl } from '@/utils/fileUrl'
import SupplierReviews from '@/components/SupplierReviews.vue'
import { echo } from '@/echo.js'
import {
  pollConversation,
  setLastMessageId,
  POLL_INTERVAL_MS,
} from '@/utils/chatStore'
export default {
  name: 'SupplierPublicProfile',
  components: {
    LoadingBar,
    SupplierReviews,
  },
  data() {
    return {
      loading: true,
      loadError: '',
      profile: null,
      materials: [],
      services: [],
      products: [],

      // ---- Chat widget ----
      chatOpen: false,
      conversationId: null,
      starting: false,
      sending: false,
      loadingHistory: false,
      chatError: '',
      draftMessage: '',
      pendingFiles: [],
      messages: [],
      currentConversationId: null,
      isLoggedIn: !!localStorage.getItem('supplier_id'),
      mySupplierId:  localStorage.getItem('supplier_id'),
      mySupplierStatus: null,
      addingSupplier: false,
      pollTimer: null,
    }
  },

  computed: {

    location() {
      if (!this.profile) return ''
      return [this.profile.street_city, this.profile.state, this.profile.country].filter(Boolean).join(', ')
    },

    memberSince() {
      if (!this.profile || !this.profile.created_at) return ''
      const d = new Date(this.profile.created_at)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    },

    contactName() {
      if (!this.profile) return ''
      return [this.profile.first_name, this.profile.last_name].filter(Boolean).join(' ')
    },

    canSend() {
      return !!this.conversationId && !this.sending && (this.draftMessage.trim().length > 0 || this.pendingFiles.length > 0)
    },
    initials() {
      if (!this.profile) return ''
      return (this.profile.company_name || '')
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    },

    mySupplierButtonLabel() {
      if (this.addingSupplier) return 'Sending request…'
      if (this.mySupplierStatus === 'approved') return 'Added In My Network'
      if (this.mySupplierStatus === 'pending') return 'Request Pending'
      if (this.mySupplierStatus === 'rejected') return 'Request Declined'
      return 'Add to Network'
    },

    mySupplierButtonIcon() {
      if (this.mySupplierStatus === 'approved') return 'fa-check'
      if (this.mySupplierStatus === 'pending') return 'fa-clock'
      if (this.mySupplierStatus === 'rejected') return 'fa-ban'
      return 'fa-user-plus'
    },

    mySupplierButtonDisabled() {
      return this.addingSupplier || !!this.mySupplierStatus
    },

  },
  async mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.isLoggedIn = !!localStorage.getItem('supplier_id');
    const externalId = this.$route.params.external_id
    if (!externalId) {
      this.loadError = 'No profile specified.'
      this.loading = false
      return
    }
    await this.loadProfile(externalId)

  },

 beforeUnmount() {
  this.stopListening()
  this.stopPolling() // NEW
},

  methods: {
    async loadProfile(externalId) {
      this.loading = true
      this.loadError = ''

      const result = await form_api_submit({
        url: `supplier/public-profile/${externalId}`,
        method: 'GET',
        data: { include_materials: true, include_services: true, include_products: true },
        show_success_toast: false,
        show_error_toast: false,
      })

      if (result.success) {
        const data = result.data || {}
        this.profile = {
          id: data.id,
          external_id: data.external_id,
          first_name: data.first_name,
          last_name: data.last_name,
          company_name: data.company_name,
          street_city: data.street_city,
          state: data.state,
          country: data.country,
          created_at: data.created_at,
          photo: data.photo,
        }
        console.log(this.isLoggedIn)
      this.materials = (data.materials || []).map((m) => ({
            id: m.id,
            name: m.name,
            type: m.type,
            pack_size: m.pack_size,
            lead_time: m.lead_time,
            sub_ingredients: m.sub_ingredients,
            documents: (m.get_attachments || []).map((file) => ({
              id: file.id,
              name: file.name,
              org_name: file.org_name,
              path: file.name, // or file.path depending on your structure
              type: file.type
            }))
          }))
        this.products = data.products || []
        this.services = data.services || []
      } else if (result?.data?.error_type === 'not_found') {
        this.loadError = 'This profile could not be found.'
      } else {
        this.loadError = 'Could not load this profile. Please try again.'
      }
      this.checkMySupplierStatus()
      this.loading = false
    },
    formatProductionType(type) {
      const map = {
        rm: 'Raw Material',
        wip: 'Work In Progress',
        fp: 'Finished Product',
      }

      return map[type] || this.formatType(type)
    },
    formatType(type) {
      if (!type) return '—'
      return type
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    },
   async loadExistingConversation() {
  if (!this.mySupplierId || !this.profile) return

  this.loadingHistory = true

  const result = await form_api_submit({
    url: `chat/${this.conversationId}/messages`,
    method: 'GET',
    data: { sender_id: this.mySupplierId },
    show_success_toast: false,
    show_error_toast: false,
  })

  this.loadingHistory = false
  if (!result.success || !Array.isArray(result.data)) return

  this.messages = this.mapAllConversationMessages(result.data, this.mySupplierId)

  // NEW — seed the polling baseline
  if (result.data.length) {
    setLastMessageId(this.conversationId, result.data[result.data.length - 1].id)
  }

  this.$nextTick(this.scrollMessagesToEnd)
},
startPolling() {
  this.stopPolling()
  this.pollTimer = setInterval(() => {
    this.pollChatWidget()
  }, POLL_INTERVAL_MS)
},

stopPolling() {
  if (this.pollTimer) {
    clearInterval(this.pollTimer)
    this.pollTimer = null
  }
},

async pollChatWidget() {
  if (!this.conversationId) return

  const newRows = await pollConversation(this.conversationId)
  if (!newRows.length) return

  const mapped = this.mapAllConversationMessages(newRows, this.mySupplierId)

  // Dedup against whatever Echo may have already pushed in
  const existingKeys = new Set(
    this.messages.map((m) => m.id).filter(Boolean)
  )
  const toAppend = mapped.filter((m) => !m.id || !existingKeys.has(m.id))

  if (!toAppend.length) return

  this.messages.push(...toAppend)
  this.$nextTick(this.scrollMessagesToEnd)
},
async sendMessage() {
  if (!this.canSend) return

  const text = this.draftMessage.trim()

  this.sending = true

  const fd = new FormData()
  fd.append('body', text)
  fd.append('sender_id', localStorage.getItem('supplier_id'))
  if (this.pendingFiles.length) {
    fd.append('attachment', this.pendingFiles[0]);
  }

  const result = await form_api_submit({
    url: `chat/${this.conversationId}/sendmessages`,
    method: 'POST',
    data: fd,
    show_success_toast: false,
    show_error_toast: true,
  })

  if (result.success) {
    const newMsg = result.data?.message || {
      mine: true,
      text,
      attachment: this.pendingFiles.length
        ? {
          name: this.pendingFiles[0].name,
          url: URL.createObjectURL(this.pendingFiles[0]),
          type: this.pendingFiles[0].type,
        }
        : null,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }

    this.messages.push({ id: result.data?.id, ...newMsg })

    // NEW — seed baseline so polling doesn't re-append our own just-sent message
    if (result.data?.id) {
      setLastMessageId(this.conversationId, result.data.id)
    }

    this.draftMessage = ''
    this.pendingFiles = []
    this.$nextTick(this.scrollMessagesToEnd)
  }

  this.sending = false
},
listenForNewMessages(conversationId) {
  this.currentConversationId = conversationId

  echo.private(`supplier-chat.${conversationId}`)
    .listen('.SupplierMessageSent', (eventMessage) => {
      if (Number(eventMessage.sender_supplier_id) === Number(this.mySupplierId)) return

      const alreadyExists = eventMessage.id && this.messages.some((m) => m.id === eventMessage.id)
      if (alreadyExists) return

      this.messages.push({
        id: eventMessage.id, // NEW — needed for poll-side dedup to work
        mine: false,
        text: eventMessage.body || eventMessage.text || '',
        attachment: eventMessage.attachment_url
          ? { url: this.getFileUrl(eventMessage.attachment_url), name: eventMessage.attachment_name, type: eventMessage.type }
          : null,
        time: eventMessage.created_at
          ? new Date(eventMessage.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      })
      this.$nextTick(this.scrollMessagesToEnd)
    })
},
    getFileUrl(filePath) {
      return resolveAssetFileUrl(filePath)
    },
    mapAllConversationMessages(messages, mySupplierId) {
      const sortedMessages = [...messages].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      )

      return sortedMessages.map((msg) => ({
        mine: Number(msg.sender_supplier_id) === Number(mySupplierId),

        text: msg.body || '',

        attachment: msg.attachment_url
          ? {
            url: this.getFileUrl(msg.attachment_url),
            name: msg.attachment_name,
            type: msg.type,
          }
          : null,

        time: msg.created_at
          ? new Date(msg.created_at).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })
          : '',
      }))
    },
    toggleChat() {
      this.chatOpen = !this.chatOpen
      if (this.chatOpen && !this.conversationId && !this.starting) {
        this.startConversation()

      }
      if (this.chatOpen) {
        this.$nextTick(this.scrollMessagesToEnd)
      }
    },
  async startConversation() {
  this.starting = true
  this.chatError = ''

  const result = await form_api_submit({
    url: 'chat/start',
    method: 'POST',
    data: {
      target_supplier_id: this.profile.id,
      sender_id: localStorage.getItem('supplier_id')
    },
    show_success_toast: false,
    show_error_toast: false,
  })

  if (result.success) {
    this.conversationId = result.data?.id ?? null

    if (this.conversationId) {
      this.listenForNewMessages(this.conversationId)
    }

    if (Array.isArray(result.data?.messages)) {
      this.messages = result.data.messages
    }
    await this.loadExistingConversation()
    this.startPolling() // NEW — start polling as a fallback once we have a real conversationId
    this.$nextTick(this.scrollMessagesToEnd)
  } else {
    this.chatError = result?.message || 'Could not start the conversation. Please try again.'
  }

  this.starting = false
},
    onFilesSelected(event) {
      const file = event.target.files[0];

      if (!file) return;

      this.pendingFiles = [file];

      // Allow selecting the same file again later
      event.target.value = '';
    },
    removeFile() {
      this.pendingFiles = [];
    },

    scrollMessagesToEnd() {
      const list = this.$refs.messageList
      if (list) list.scrollTop = list.scrollHeight
    },
    stopListening(conversationId = this.currentConversationId) {
      if (!conversationId) return

      echo.leave(`supplier-chat.${conversationId}`)
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = document.getElementById('messages-container') // <-- doesn't exist
        if (container) container.scrollTop = container.scrollHeight
      })
    },
    async checkMySupplierStatus() {
      if (!this.isLoggedIn || !this.profile) return

      const result = await form_api_submit({
        url: 'my-suppliers/status',
        method: 'GET',
        data: {
          supplier_id: this.mySupplierId,
          target_supplier_id: this.profile.id,
        },
        show_success_toast: false,
        show_error_toast: false,
      })

      if (result.success) {
        this.mySupplierStatus = result.data?.status || null
      }
    },
    async addToMySuppliers() {
      if (!this.isLoggedIn || this.addingSupplier || this.mySupplierStatus) return

      this.addingSupplier = true

      const result = await form_api_submit({
        url: 'my-suppliers/store',
        method: 'POST',
        data: {
          supplier_id: this.mySupplierId,
          target_supplier_id: this.profile.id,
        },
        show_success_toast: true,
        show_error_toast: true,
      })

      this.addingSupplier = false

      if (result.success) {
        this.mySupplierStatus = result.data?.status || 'pending'
      }
    },
  },
}
</script>

<style scoped>
.supplier-public-profile {
  width: 100%;
  min-height: 100vh;
  background: var(--color-surface);
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.supplier-public-profile * {
  box-sizing: border-box;
}

.profile-status {
  padding: 80px 24px;
  text-align: center;
  font-size: 13.5px;
  color: var(--color-text-muted);
}

.profile-status--error {
  color: #b3261e;
}

.profile-banner {
  background: var(--color-tint-light);
  border-bottom: 1px solid var(--color-border);
  padding: 26px 24px;
}

.profile-banner__inner {
  max-width: 900px;
  margin: 0 auto;
}

.profile-banner__identity {
  display: flex;
  align-items: center;
  gap: 16px;
}

.profile-banner__avatar {
  width: 100px;
  height: 80px;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
}

.profile-banner__name {
  margin: 0 0 3px;
  font-size: 21px;
  font-weight: 800;
  color: var(--color-text-dark);
}

.profile-banner__meta {
  margin: 0;
  font-size: 12.5px;
  color: var(--color-text-muted);
}

.profile-banner__dot {
  margin: 0 4px;
  opacity: 0.6;
}

.profile-banner__contact {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--color-text-muted);
}

.profile-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px 24px 56px;
}

.profile-section {
  margin-bottom: 4px;
}

.section-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 22px 0 12px;
  padding-top: 18px;
  border-top: 1px solid var(--color-border);
}

.profile-container>.profile-section:first-of-type .section-head {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.section-head__icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background: var(--color-tint-light);
  display: grid;
  place-items: center;
  font-size: 14px;
}

.section-head__title {
  margin: 0 0 1px;
  font-size: 13.5px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.section-head__desc {
  margin: 0;
  font-size: 11.5px;
  color: var(--color-text-muted);
}

.profile-empty {
  margin: 0;
  font-size: 12.5px;
  color: var(--color-text-muted);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

.data-table thead th {
  text-align: left;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--color-text-muted);
  padding: 0 10px 8px;
  border-bottom: 1px solid var(--color-border);
}

.data-table tbody td {
  padding: 9px 10px;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-dark);
  vertical-align: top;
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.data-table tbody tr:hover td {
  background: var(--color-tint-light);
}

.data-table__strong {
  font-weight: 700;
}

.data-table__muted {
  color: var(--color-text-muted);
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-dark);
}

@media (max-width: 760px) {
  .data-table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
}

@media (max-width: 480px) {
  .profile-banner__identity {
    flex-direction: column;
    align-items: flex-start;
  }
}

.chat-fab {
  position: fixed;
  right: 32px;
  bottom: 32px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  border: none;
  border-radius: 50px;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  cursor: pointer;
  box-shadow: 0 8px 30px -8px rgba(12, 18, 83, 0.5);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  z-index: 999;
  font-family: inherit;
}

.chat-fab svg {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.chat-fab__label {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.chat-fab:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 12px 36px -8px rgba(12, 18, 83, 0.6);
}

.chat-fab:active {
  transform: scale(0.96);
}

.chat-fab.is-open {
  background: var(--color-text-dark);
  box-shadow: 0 8px 30px -8px rgba(52, 19, 60, 0.4);
}

.chat-fab.is-open .chat-fab__label {
  display: none;
}

.chat-fab.is-open {
  width: 54px;
  height: 54px;
  padding: 0;
  justify-content: center;
  border-radius: 50%;
}

/* Pulse animation to draw attention */
.chat-fab::before {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 50px;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  opacity: 0.3;
  z-index: -1;
  animation: pulse 2s ease-in-out infinite;
}

.chat-fab.is-open::before {
  display: none;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.4;
  }

  50% {
    transform: scale(1.08);
    opacity: 0.1;
  }

  100% {
    transform: scale(1);
    opacity: 0.4;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .chat-fab {
    right: 20px;
    bottom: 20px;
    padding: 12px 18px;
  }

  .chat-fab__label {
    font-size: 13px;
  }

  .chat-fab svg {
    width: 20px;
    height: 20px;
  }
}

@media (max-width: 480px) {
  .chat-fab {
    right: 16px;
    bottom: 16px;
    padding: 10px 16px;
  }

  .chat-fab__label {
    font-size: 12px;
  }

  .chat-fab svg {
    width: 18px;
    height: 18px;
  }
}
.chat-panel {
  position: fixed;
  right: 32px;
  bottom: 100px;
  width: 380px;
  max-width: calc(100vw - 64px);
  height: 500px;
  max-height: calc(100vh - 140px);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  box-shadow: 0 24px 50px -20px rgba(12, 18, 83, 0.35);
  display: flex;
  flex-direction: column;
  z-index: 998;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow: hidden;
}

.chat-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
  background: var(--color-surface);
}

.chat-panel__identity {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chat-panel__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.chat-panel__name {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.chat-panel__status {
  display: block;
  font-size: 11px;
  color: var(--color-text-muted);
}

.chat-panel__close {
  border: none;
  background: transparent;
  font-size: 16px;
  cursor: pointer;
  color: var(--color-text-muted);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}

.chat-panel__close:hover {
  background: var(--color-tint-light);
  color: var(--color-text-dark);
}

.chat-panel__messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #faf8f9;
}

.chat-panel__empty {
  color: var(--color-text-muted);
  font-size: 13px;
  text-align: center;
  margin-top: 20px;
}

.chat-panel__empty--error {
  color: #b3261e;
}

.chat-panel__retry {
  display: block;
  margin: 10px auto 0;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-dark);
  cursor: pointer;
  transition: background 0.15s ease;
}

.chat-panel__retry:hover {
  background: var(--color-tint-light);
}

.chat-msg {
  align-self: flex-start;
  max-width: 82%;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 12px 12px 12px 4px;
  padding: 8px 10px;
  font-size: 12px;
  color: var(--color-text-dark);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.chat-msg--mine {
  align-self: flex-end;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  border-color: transparent;
  border-radius: 12px 12px 4px 12px;
}

.chat-msg__text {
  margin: 0 0 4px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.chat-msg__files {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 4px;
}

.chat-msg__file {
  font-size: 12px;
  opacity: 0.9;
  color: #fff;
}

.chat-msg__time {
  display: block;
  font-size: 10px;
  opacity: 0.6;
  text-align: right;
  margin-top: 2px;
}

.chat-msg--mine .chat-msg__time {
  opacity: 0.8;
}

.chat-panel__composer {
  border-top: 1px solid var(--color-border);
  padding: 12px 16px;
  flex-shrink: 0;
  background: var(--color-surface);
}

.chat-panel__file-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.chat-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 12px;
  background: var(--color-tint-light);
  border: 1px solid var(--color-border);
  color: var(--color-text-dark);
}

.chat-file-chip button {
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--color-text-muted);
  font-size: 12px;
  padding: 0 2px;
}

.chat-file-chip button:hover {
  color: #b3261e;
}

.chat-panel__input-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.chat-panel__attach {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}

.chat-panel__attach:hover {
  background: var(--color-tint-light);
}

.chat-panel__attachment-preview {
  margin-bottom: 12px;
}

.attachment-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-surface);
}

.attachment-preview-image {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid var(--color-border);
  flex-shrink: 0;
}

.attachment-preview-file {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-tint-light);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
  font-size: 22px;
  flex-shrink: 0;
}

.attachment-info {
  flex: 1;
  min-width: 0;
}

.attachment-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.attachment-size {
  margin-top: 2px;
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.attachment-remove {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background .15s ease, color .15s ease;
}

.attachment-remove:hover {
  background: var(--color-tint-light);
  color: #dc2626;
}

.chat-panel__file-input {
  display: none;
}

.chat-panel__textarea {
  flex: 1;
  resize: none;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  color: var(--color-text-dark);
  outline: none;
  transition: border-color 0.15s ease;
  min-height: 38px;
  max-height: 80px;
  line-height: 1.5;
}

.chat-panel__textarea:focus {
  border-color: var(--color-secondary);
  box-shadow: 0 0 0 3px rgba(11, 23, 153, 0.08);
}

.chat-panel__textarea:disabled {
  background: var(--color-tint-light);
  color: var(--color-text-muted);
}

.chat-panel__send {
  flex-shrink: 0;
  height: 38px;
  padding: 0 18px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;
}

.chat-panel__send:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px -6px rgba(12, 18, 83, 0.4);
}

.chat-panel__send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.chat-panel__send:active:not(:disabled) {
  transform: scale(0.96);
}

/* Chat pop animations */
.chat-pop-enter-active,
.chat-pop-leave-active {
  transition: opacity 0.2s ease, transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.chat-pop-enter-from,
.chat-pop-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.96);
}

/* Scrollbar styling */
.chat-panel__messages::-webkit-scrollbar {
  width: 4px;
}

.chat-panel__messages::-webkit-scrollbar-track {
  background: transparent;
}

.chat-panel__messages::-webkit-scrollbar-thumb {
  background: var(--color-border);
  border-radius: 4px;
}

.chat-panel__messages::-webkit-scrollbar-thumb:hover {
  background: var(--color-text-muted);
}

/* Responsive */
@media (max-width: 768px) {
  .chat-panel {
    right: 20px;
    bottom: 90px;
    width: 340px;
    height: 440px;
    max-height: calc(100vh - 120px);
  }
}

@media (max-width: 480px) {
  .chat-panel {
    right: 12px;
    bottom: 80px;
    width: calc(100vw - 24px);
    height: 420px;
    max-height: calc(100vh - 110px);
    border-radius: 12px;
  }

  .chat-panel__header {
    padding: 12px 14px;
  }

  .chat-panel__messages {
    padding: 12px 14px;
  }

  .chat-panel__composer {
    padding: 10px 12px;
  }
}

.profile-banner__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
}

.profile-banner__identity {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.app-header__support {
  margin-left: auto;
  flex-shrink: 0;
}

.profile-banner__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

@media (max-width: 480px) {
  .profile-banner__actions {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
}
.profile-container .supplier-reviews {
  margin-top: 2rem;
}
.supplier-reviews .star {
  color: #d1d5db;
}

.supplier-reviews .star--filled {
  color: #f59e0b;
}

/* Style the review form to match your theme */
.supplier-reviews .review-form {
  background: var(--color-tint-light);
  border-color: var(--color-border);
}

.supplier-reviews .review-form__textarea {
  background: var(--color-surface);
  border-color: var(--color-border);
}

.supplier-reviews .review-form__textarea:focus {
  border-color: var(--color-secondary);
}
</style>